import { Newspaper, Presentation, Users, Handshake, CalendarDays, BadgeCheck, CalendarHeart, Sparkles } from "lucide-react";
import { useConfig } from "@/context/ConfigContext";
import { useLang } from "@/context/LanguageContext";
import PublicLayout from "@/components/PublicLayout";
import PathHero from "@/components/PathHero";
import SalesGrid from "@/components/sections/SalesGrid";
import ClubForm from "@/components/forms/ClubForm";
import { Reveal, Overline } from "@/components/Reveal";

const CLUB_IMG =
  "https://images.unsplash.com/photo-1675716921224-e087a0cca69a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMG5ldHdvcmtpbmclMjBldmVudCUyMHByb2Zlc3Npb25hbHMlMjBtZWV0aW5nfGVufDB8fHx8MTc5MTIwNDkyMHww&ixlib=rb-4.1.0&q=85";
const BENEFIT_ICONS = [Newspaper, Presentation, Users, Handshake, CalendarDays, BadgeCheck];

export default function ClubPage() {
  const { t } = useLang();
  const config = useConfig();
  const c = t.club;

  return (
    <PublicLayout variant="club">
      <PathHero
        image={CLUB_IMG}
        badge={c.hero.badge}
        title={c.hero.title}
        subtitle={c.hero.subtitle}
        cta={c.hero.cta}
        note={c.hero.note}
        trust={c.hero.trust}
      />

      {/* Free-until notice */}
      <div className="bg-brand text-white" data-testid="club-free-notice">
        <div className="mx-auto max-w-5xl px-4 md:px-8 py-3.5 flex items-center justify-center gap-2.5 text-center">
          <Sparkles size={17} strokeWidth={2.25} className="shrink-0" />
          <p className="text-sm md:text-base font-medium">{c.freeNotice}</p>
        </div>
      </div>

      {/* Intro */}
      <section className="bg-paper py-20 md:py-28 border-t border-line">
        <div className="mx-auto max-w-3xl px-4 md:px-8 text-center">
          <Reveal>
            <div className="flex justify-center"><Overline>{c.intro.overline}</Overline></div>
            <h2 className="mt-4 font-serif font-semibold text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.08]">
              {c.intro.title}
            </h2>
            <p className="mt-6 text-stone text-base md:text-lg leading-relaxed">{c.intro.p1}</p>
            <p className="mt-4 text-stone text-base md:text-lg leading-relaxed">{c.intro.p2}</p>
          </Reveal>
        </div>
      </section>

      <SalesGrid overline={c.benefits.overline} title={c.benefits.title} cards={c.benefits.items} icons={BENEFIT_ICONS} surface />

      {/* Membership form */}
      <section id="form" className="bg-paper py-20 md:py-28 border-t border-line scroll-mt-20" data-testid="club-form-section">
        <div className="mx-auto max-w-2xl px-4 md:px-8">
          <Reveal className="text-center">
            <div className="flex justify-center"><Overline>{c.form.overline}</Overline></div>
            <h2 className="mt-4 font-serif font-semibold text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.08]">
              {c.form.title}
            </h2>
          </Reveal>
          <Reveal delay={0.08} className="mt-10">
            <div className="bg-paper border border-line rounded-3xl p-6 md:p-9 shadow-sm">
              <ClubForm config={config} />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 2027 Events band */}
      <section className="bg-ink text-white py-20 md:py-24 border-t border-line">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 h-12 w-12 rounded-2xl bg-brand text-white justify-center mx-auto">
              <CalendarHeart size={24} strokeWidth={2} />
            </span>
            <div className="mt-6 text-xs font-bold uppercase tracking-[0.18em] text-brand">{c.events.overline}</div>
            <h2 className="mt-3 font-serif font-semibold text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.08]">
              {c.events.title}
            </h2>
            <p className="mt-6 text-white/75 text-base md:text-lg leading-relaxed">{c.events.body}</p>
          </Reveal>
        </div>
      </section>
    </PublicLayout>
  );
}
